/**
 * Atalhos de teclado (2026-10-16), no padrão do hub da TechIA (e de Linear,
 * GitHub, Notion): modificador + tecla só pro que precisa valer de qualquer
 * lugar (⌘B, o menu); SEQUÊNCIA de duas teclas pra navegar (G e depois a
 * letra da tela); ? abre a lista. Letra solta nunca dispara dentro de campo de
 * texto. Registro único: alimenta o ouvinte global (`shell/KeyboardShortcuts`)
 * e a lista que aparece no ?.
 *
 * `nav` é o RÓTULO do item do menu (`shell/navigation.ts`): o destino, o papel
 * mínimo e a exigência de CENA vêm de lá, então o atalho nunca leva a uma tela
 * que o menu esconderia.
 */

export type ShortcutGroup = "geral" | "navegar";

export type Shortcut = {
  id: string;
  group: ShortcutGroup;
  /** Na ordem em que são pressionadas. `mod` = ⌘ no Mac, Ctrl no resto. */
  keys: string[];
  /** Uma depois da outra (G e depois M), não juntas. */
  sequence?: true;
  label: string;
  nav?: string;
  /** Ação que não é ir pra uma tela: abrir a Mestra Help do canto. */
  action?: "mestra";
};

export const SHORTCUT_GROUPS: Array<{ key: ShortcutGroup; label: string; hint?: string }> = [
  { key: "geral", label: "Geral" },
  { key: "navegar", label: "Ir para", hint: "Pressione G e, em seguida, a letra." }
];

export const SHORTCUTS: Shortcut[] = [
  { id: "menu", group: "geral", keys: ["mod", "B"], label: "Recolher ou expandir o menu" },
  { id: "ajuda", group: "geral", keys: ["?"], label: "Ver todos os atalhos" },
  { id: "fechar", group: "geral", keys: ["Esc"], label: "Fechar janela aberta" },

  { id: "ir-inicio", group: "navegar", sequence: true, keys: ["G", "I"], label: "Início", nav: "Início" },
  { id: "ir-empresas", group: "navegar", sequence: true, keys: ["G", "E"], label: "Empresas", nav: "Empresas" },
  { id: "ir-visao-geral", group: "navegar", sequence: true, keys: ["G", "V"], label: "Visão geral", nav: "Visão geral" },
  { id: "abrir-mestra", group: "navegar", sequence: true, keys: ["G", "M"], label: "Mestra Help", action: "mestra" },
  { id: "ir-mencoes", group: "navegar", sequence: true, keys: ["G", "N"], label: "Análises e menções", nav: "Análises e menções" },
  // As três telas de 2026-10-18/20 entraram no menu e ficaram sem atalho até
  // 2026-10-22; `keyboard-shortcuts.test.tsx` passou a acusar item do menu
  // sem atalho.
  { id: "ir-justificativas", group: "navegar", sequence: true, keys: ["G", "J"], label: "Justificativas", nav: "Justificativas" },
  { id: "ir-lixeira", group: "navegar", sequence: true, keys: ["G", "L"], label: "Lixeira", nav: "Lixeira" },
  { id: "ir-historico-edicoes", group: "navegar", sequence: true, keys: ["G", "H"], label: "Histórico de edições", nav: "Histórico de edições" },
  { id: "ir-uploads", group: "navegar", sequence: true, keys: ["G", "U"], label: "Uploads", nav: "Uploads" },
  { id: "ir-reprocessamentos", group: "navegar", sequence: true, keys: ["G", "P"], label: "Reprocessamentos", nav: "Reprocessamentos" },
  { id: "ir-ajustes", group: "navegar", sequence: true, keys: ["G", "A"], label: "Ajustes", nav: "Ajustes" },
  { id: "ir-dashboard", group: "navegar", sequence: true, keys: ["G", "D"], label: "Dashboard", nav: "Dashboard" },
  { id: "ir-relatorios", group: "navegar", sequence: true, keys: ["G", "R"], label: "Amostras", nav: "Amostras" },
  { id: "ir-downloads", group: "navegar", sequence: true, keys: ["G", "B"], label: "Downloads", nav: "Downloads" }
];

/** Quanto tempo a primeira tecla da sequência fica esperando a segunda. */
export const SEQUENCE_TIMEOUT_MS = 1200;

export function isMac(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return /mac|iphone|ipad/i.test(nav.userAgentData?.platform || nav.platform || nav.userAgent || "");
}

export function keyLabel(key: string, mac = isMac()): string {
  if (key === "mod") return mac ? "⌘" : "Ctrl";
  return key;
}

/** Campo onde a pessoa está digitando: letra solta ali é texto, não atalho. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;
  const type = (target as HTMLInputElement).type;
  return !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(type);
}

/** O atalho de sequência que fecha com esta segunda tecla, ou nulo. */
export function sequenceFor(first: string, second: string): Shortcut | null {
  const a = first.toUpperCase();
  const b = second.toUpperCase();
  return SHORTCUTS.find(item => item.sequence && item.keys[0] === a && item.keys[1] === b) ?? null;
}

/** A tecla é o começo de alguma sequência? */
export function startsSequence(key: string): boolean {
  const k = key.toUpperCase();
  return SHORTCUTS.some(item => item.sequence && item.keys[0] === k);
}

// ── ligar e desligar (preferência do navegador) ─────────────────────────────
const ENABLED_KEY = "reputation_keyboard_shortcuts";
const CHANGE_EVENT = "reputation:keyboard-shortcuts";

export function shortcutsEnabled(): boolean {
  try {
    return window.localStorage.getItem(ENABLED_KEY) !== "0";
  } catch {
    return true;
  }
}

export function setShortcutsEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0");
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Avisa quando alguém liga ou desliga os atalhos (outra tela, outra aba). */
export function onShortcutsEnabledChange(listener: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === ENABLED_KEY) listener();
  };
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** O evento que a Topbar escuta pra abrir a Mestra Help (a caixa rápida do
 *  Copiloto no canto): o atalho não conhece o botão, só pede. */
export const OPEN_MESTRA_EVENT = "reputation:open-mestra";

/** Os atalhos de um grupo em ordem alfabética do rótulo, como as listas os
 *  mostram (a página de Preferências e a lista do ?). */
export function inDisplayOrder(items: Shortcut[]): Shortcut[] {
  return [...items].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}
